import axios from 'axios';

import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type { EvaluationPeriod } from '@/types/manager-assessment/Evaluation';
import type {
    AssessmentTemplate,
    ReviewTemplateListResponseDto,
    ReviewTemplateResponseDto,
    AssessmentTemplateQuestion,
} from '@/types/manager-assessment/Template';
import type { ManagerAssessmentCompetencyResultsResponse } from '@/types/review/CompetencyGroupResult';
import type {
    KeyCompetenciesData,
    KeyCompetenciesDescribePayload,
    KeyCompetenciesSelectPayload,
    KeyCompetenciesSelectResult,
} from '@/types/review/KeyCompetencies';

import { api } from '@/api/api';

/**
 * What each predefined evaluation period is called. Translation keys rather than words: the reader
 * translates them, and they are the same names whether a period is being chosen for a new cycle or
 * read back off an existing one.
 */
export const PREDEFINED_EVALUATION_PERIOD_NAMES: Record<string, string> = {
    annual: 'managerAssessmentSetup.evaluationPeriod.annual',
    'first-half': 'managerAssessmentSetup.evaluationPeriod.firstHalf',
    'second-half': 'managerAssessmentSetup.evaluationPeriod.secondHalf',
};

export interface ManagerAssessmentCompetencyPayload {
    competencyId: string;
    competencyGroupId?: string | null;
}

/** Manager assessment setup (Feature G, Steps 1–5) persisted onto a CycleInstance. */
export interface ManagerAssessmentPayload {
    assesseeId: string;
    competencies: ManagerAssessmentCompetencyPayload[];
    includeCompanyValues: boolean;
    evaluationCycleId?: string | null;
    startDate?: string | null;
    managerTemplateId?: string | null;
}

export interface ManagerAssessmentQuestionAnswerPayload {
    reviewQuestionId: string;
    hasBeenSkipped: boolean;
    textValue?: string | null;
    boolValue?: boolean | null;
    intValue?: number | null;
    selectedOptionIds: string[];
}

/** The manager's completed survey answers, saved when the assessment is submitted. */
export interface ManagerAssessmentAnswersPayload {
    answers: ManagerAssessmentQuestionAnswerPayload[];
}

/**
 * The same shape sent to the draft endpoint while the survey is still being filled in. Only the answers
 * it carries are touched server-side, so it can hold a single question rather than the whole survey.
 */
export type ManagerAssessmentDraftAnswersPayload = ManagerAssessmentAnswersPayload;

export interface MeetingTopicPayload {
    /** Set for an existing topic being updated; omit for a new one. */
    id?: string | null;
    subject: string;
    notes?: string | null;
    sortOrder: number;
}

/** The manager's meeting discussion points — replaces the full set on the CycleInstance. */
export interface MeetingTopicsPayload {
    topics: MeetingTopicPayload[];
}

/**
 * The evaluation date + period. evaluationPeriodId holds a predefined period's id (or the "custom"
 * sentinel); start/end are set only for a custom range.
 */
export interface EvaluationPeriodPayload {
    evaluationDate?: string | null;
    evaluationPeriodId?: string | null;
    evaluationPeriodStart?: string | null;
    evaluationPeriodEnd?: string | null;
}

export interface CompetencyProficiencyPayload {
    competencyId: string;
    proficiencyLevelId?: string | null;
}

export interface CompanyValueProficiencyPayload {
    companyValueId: string;
    proficiencyLevelId?: string | null;
}

/** Quick assessment: a proficiency level assigned to each competency (and company value). */
export interface QuickAssessmentPayload {
    assessments: CompetencyProficiencyPayload[];
    companyValues: CompanyValueProficiencyPayload[];
}

// Evaluation periods and templates now use the existing ReviewTemplate endpoints
const REVIEW_TEMPLATE_BASE_URL = '/ReviewTemplate';

/** One question to generate an AI insight for; the caller owns the correlationId. */
export interface ManagerAssessmentInsightQuestionPayload {
    questionId: string;
    questionText: string;
    competency: string;
    correlationId: string;
}

/** Fires AI insight generation for all questions of a manager assessment (results stream via SignalR). */
export interface ManagerAssessmentInsightsPayload {
    cycleInstanceId: string;
    /** The evaluation period bounds (ISO-8601); window the engagement report. Required. */
    fromDate: string;
    toDate: string;
    questions: ManagerAssessmentInsightQuestionPayload[];
}

export interface ManagerAssessmentInsightsResponse {
    /** "accepted" = at least one question was dispatched; "failed" = the gateway rejected the dispatch. */
    status: 'accepted' | 'failed';
    count: number;
    generated: number;
    cached: number;
    /** Present when status is "failed" — why the AI gateway rejected the dispatch. */
    error?: string;
    errorCode?: string;
    /**
     * Questions that were already in flight: the result will arrive under the returned correlationId (the
     * running job's), so the caller should subscribe to that instead of the one it sent — avoiding a
     * duplicate gateway call on a re-trigger.
     */
    attached: { questionId: string; correlationId: string }[];
}

export default {
    /**
     * Triggers per-question AI insight generation; results come back over SignalR. The response reports which
     * questions were already in flight (`attached`) so the caller can subscribe to the running correlationId.
     */
    async requestInsights(
        payload: ManagerAssessmentInsightsPayload,
    ): Promise<ManagerAssessmentInsightsResponse> {
        const response = await api.post('/ManagerAssessment/insights', payload);
        return response.data;
    },

    async saveManagerAssessment(
        cycleInstanceId: string,
        payload: ManagerAssessmentPayload,
    ): Promise<CycleInstance> {
        const response = await api.put(
            `/cycleinstance/${cycleInstanceId}/manager-assessment`,
            payload,
        );

        return response.data;
    },

    async saveManagerAssessmentAnswers(
        cycleInstanceId: string,
        payload: ManagerAssessmentAnswersPayload,
    ): Promise<CycleInstance> {
        const response = await api.put(
            `/cycleinstance/${cycleInstanceId}/manager-assessment/answers`,
            payload,
        );

        return response.data;
    },

    /**
     * Autosave for the in-progress survey. Upserts only the answers it is given and returns 204 — the
     * assessment is otherwise untouched (no stepper advance, no AI-artifact invalidation, no score or
     * proficiency-history write). Submitting the survey still goes through saveManagerAssessmentAnswers.
     */
    async saveManagerAssessmentDraftAnswers(
        cycleInstanceId: string,
        payload: ManagerAssessmentDraftAnswersPayload,
    ): Promise<void> {
        await api.put(
            `/cycleinstance/${cycleInstanceId}/manager-assessment/answers/draft`,
            payload,
        );
    },

    async saveMeetingTopics(
        cycleInstanceId: string,
        payload: MeetingTopicsPayload,
    ): Promise<CycleInstance> {
        const response = await api.put(
            `/cycleinstance/${cycleInstanceId}/manager-assessment/meeting-topics`,
            payload,
        );

        return response.data;
    },

    async saveEvaluationPeriod(
        cycleInstanceId: string,
        payload: EvaluationPeriodPayload,
    ): Promise<CycleInstance> {
        const response = await api.put(
            `/cycleinstance/${cycleInstanceId}/manager-assessment/evaluation-period`,
            payload,
        );

        return response.data;
    },

    async saveQuickAssessment(
        cycleInstanceId: string,
        payload: QuickAssessmentPayload,
    ): Promise<CycleInstance> {
        const response = await api.put(
            `/cycleinstance/${cycleInstanceId}/manager-assessment/quick-assessment`,
            payload,
        );

        return response.data;
    },

    // Advances the stepper to a reached mid-flow step when the manager opens its page — step 3
    // (Prepare Meeting) or step 4 (Meet) — so the dashboard reflects the right progress and reopens
    // the review on that step. Forward-only on the backend, so it is safe to fire on every mount.
    async advanceManagerAssessmentStep(
        cycleInstanceId: string,
        step: number,
    ): Promise<CycleInstance> {
        const response = await api.put(
            `/cycleinstance/${cycleInstanceId}/manager-assessment/step/${step}`,
        );

        return response.data;
    },

    // The periods a manager may choose from when setting a cycle up. This is a writing vocabulary,
    // not a reading filter: a page that filters existing evaluations asks the server which periods
    // the employee actually has, through `useGetEmployeeEvaluationPeriods`.
    getEvaluationPeriods(): Promise<EvaluationPeriod[]> {
        const year = new Date().getFullYear();

        return Promise.resolve([
            {
                id: 'annual',
                name: PREDEFINED_EVALUATION_PERIOD_NAMES.annual,
                startDate: `${year}-01-01`,
                endDate: `${year}-12-31`,
            },
            {
                id: 'first-half',
                name: PREDEFINED_EVALUATION_PERIOD_NAMES['first-half'],
                startDate: `${year}-01-01`,
                endDate: `${year}-06-30`,
            },
            {
                id: 'second-half',
                name: PREDEFINED_EVALUATION_PERIOD_NAMES['second-half'],
                startDate: `${year}-07-01`,
                endDate: `${year}-12-31`,
            },
        ]);
    },

    /**
     * The manager-assessment setup picker only exposes company templates.
     *
     * App-wide/system templates belong to the dedicated app-wide library flow and must not
     * appear in this company picker (Bug #15185).
     */
    async getTemplates(): Promise<AssessmentTemplate[]> {
        const { data } = await api.get<ReviewTemplateListResponseDto>(
            `${REVIEW_TEMPLATE_BASE_URL}/ByReviewType?reviewType=ManagerEvaluation`,
        );

        return (data.items || []).map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            questions: item.questions?.length ?? 0,
            accessType: item.accessType,
        }));
    },

    async getTemplateQuestions(templateId: string): Promise<AssessmentTemplateQuestion[]> {
        const { data } = await api.get<ReviewTemplateResponseDto>(
            `${REVIEW_TEMPLATE_BASE_URL}/${templateId}`,
        );
        return data.questions || [];
    },

    async updatePerformanceScore(
        reviewInstanceId: string,
        overallPerformanceLevelId: string,
    ): Promise<void> {
        await api.put(`/reviewinstance/${reviewInstanceId}/performance-score`, {
            overallPerformanceLevelId,
        });
    },

    /**
     * Requests the AI summary for a cycle. If one is already saved it is returned inline
     * (status "cached", with content); otherwise generation is queued (status "accepted") and the result
     * arrives over SignalR under the correlationId.
     */
    async generateSummary(payload: GenerateSummaryPayload): Promise<GenerateSummaryResponse> {
        const response = await api.post('/ManagerAssessment/summary', payload);
        return response.data;
    },

    /**
     * Triggers AI meeting-agenda generation for a cycle; the result comes back over SignalR. Returns the
     * correlationId to subscribe to: normally the one sent, but an already in-flight request (status
     * "pending") returns its own correlationId so the caller attaches to it instead of starting a new job.
     */
    async generateAgenda(payload: GenerateAgendaPayload): Promise<GenerateAgendaResponse> {
        const response = await api.post('/ManagerAssessment/agenda', payload);
        return response.data;
    },

    /**
     * Triggers AI "Recommendations for your development" generation from the assessment data and the
     * manager's two answers; the result (JSON) comes back over SignalR under the correlationId.
     */
    async generateRecommendations(payload: GenerateRecommendationsPayload): Promise<void> {
        await api.post('/ManagerAssessment/recommendations', payload);
    },

    /**
     * Starts — or attaches to — the AI recap of the cycle's uploaded transcript. The recap itself arrives
     * over SignalR under the correlation id, so this only reports which one to listen on.
     */
    async generateRecap(payload: GenerateRecapPayload): Promise<GenerateRecapResponse> {
        const { data } = await api.post<GenerateRecapResponse>('/ManagerAssessment/recap', payload);
        return data;
    },

    async getCompetencyResults(
        cycleInstanceId: string,
    ): Promise<ManagerAssessmentCompetencyResultsResponse> {
        const { data } = await api.get<ManagerAssessmentCompetencyResultsResponse>(
            `/managerassessment/competency-results/${cycleInstanceId}`,
        );
        return data;
    },

    /**
     * The manager's Mastered/Growth lists for a cycle. The backend returns the saved lists, or — when nothing
     * is saved yet — seeds them from the code-side selection + the Results summary's descriptions. Returns
     * null (404) when the cycle has no score to select from.
     */
    async getKeyCompetencies(cycleInstanceId: string): Promise<KeyCompetenciesData | null> {
        try {
            const { data } = await api.get<KeyCompetenciesData>(
                `/ManagerAssessment/keycompetencies/${cycleInstanceId}`,
            );
            return data;
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                return null;
            }
            throw error;
        }
    },

    /** Autosaves the manager's two lists for a cycle. */
    async saveKeyCompetencies(cycleInstanceId: string, data: KeyCompetenciesData): Promise<void> {
        await api.put(`/ManagerAssessment/keycompetencies/${cycleInstanceId}`, data);
    },

    /**
     * Code-side Replace/Add selection (deterministic, no AI): returns the competencies to apply plus whether
     * the reserve is now exhausted (so the UI can disable the action).
     */
    async selectKeyCompetencies(
        cycleInstanceId: string,
        payload: KeyCompetenciesSelectPayload,
    ): Promise<KeyCompetenciesSelectResult> {
        const { data } = await api.post<KeyCompetenciesSelectResult>(
            `/ManagerAssessment/keycompetencies/${cycleInstanceId}/select`,
            payload,
        );
        return data;
    },

    /** Triggers AI description generation for the given competencies; the result comes back over SignalR. */
    async describeKeyCompetencies(payload: KeyCompetenciesDescribePayload): Promise<void> {
        await api.post('/ManagerAssessment/keycompetencies/describe', payload);
    },
};

/** The frontend owns the correlationId so it can match the result pushed back over SignalR. */
export interface GenerateSummaryPayload {
    cycleInstanceId: string;
    correlationId: string;
}

export interface GenerateSummaryResponse {
    /**
     * "cached" = content is present in this response; "accepted" = a new job was queued; "pending" = a
     * generation for this cycle was already in flight — attach to it via correlationId instead of starting
     * a new one.
     */
    status: 'cached' | 'accepted' | 'pending';
    /** The bilingual summary JSON, present only when status is "cached". */
    content?: string;
    /** The correlationId to subscribe to for the SignalR result ("accepted" and "pending"). */
    correlationId?: string;
}

export interface GenerateAgendaPayload {
    cycleInstanceId: string;
    correlationId: string;
    /** The manager's objective for the meeting (modal step 1, optional free text). */
    objectives?: string;
    /** Context about the employee's performance — aspects to address (modal step 2, optional free text). */
    context?: string;
}

export interface GenerateAgendaResponse {
    /** "accepted" = a new job was queued; "pending" = a generation for this cycle was already in flight. */
    status: 'accepted' | 'pending';
    /** The correlationId to subscribe to for the SignalR result. */
    correlationId?: string;
}

export interface GenerateRecommendationsPayload {
    cycleInstanceId: string;
    correlationId: string;
    /** The manager's overall feeling about the employee's performance (modal step 1). */
    overallFeeling: string;
    /** The priority the manager wants the employee to develop (modal step 2). */
    developmentPriority: string;
    /** Output language code (e.g. "en", "fr"). */
    language?: string;
}

export interface GenerateRecapPayload {
    cycleInstanceId: string;
    correlationId: string;
    /** The language the recap is written in (the UI language); the server falls back to the request culture. */
    language?: string;
}

export interface GenerateRecapResponse {
    /** "accepted" = a new job was queued; "pending" = the caller already had one in flight for the cycle. */
    status: 'accepted' | 'pending';
    /** The caller's own correlationId, or the one of the generation already running. */
    correlationId: string;
}
